import { logger } from '../utils/logger';
import { getBackendUrl } from './configService';

const log = logger('AffService');

/**
 * Every query key the backend understands, in one place.
 *
 * #771: the Lightning service builds its query string from a hand-written
 * if-chain, so a filter key that is not listed vanishes silently and the grid
 * filter does nothing. Here the URL is built by iterating this list, and a test
 * asserts that every key the adapters can emit appears in it — so adding a
 * filter without wiring it fails the suite instead of shipping broken.
 */
export const AFF_QUERY_KEYS = [
  'page',
  'limit',
  'sortBy',
  'sortOrder',
  'search',
  // Column filters (#866 U1) — Aircraft all six; Tracks name, callsign, esn.
  'name',
  'callsign',
  'make',
  'model',
  'owner',
  // Aircraft
  'operationalFunction',
  'groupDesignator',
  'stale',
  // Tracks — observation window
  'startDate',
  'endDate',
  // Tracks — capture window (a different fact; see affSchema)
  'capturedAfter',
  'capturedBefore',
  // Exact ESN; on Tracks may repeat for a multi-aircraft selection
  'esn',
  // Grids only: list SIM- aircraft too (the server honours it for aff.simulate)
  'includeSim',
] as const;

export type AffQueryKey = typeof AFF_QUERY_KEYS[number];
export type AffQueryParams = Partial<Record<AffQueryKey, any>>;

export interface AffResponse<T = any> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/**
 * The query string for AFF params, grid and export alike (#866): an array is
 * sent as the key repeated, which Express reads back as an array, so a
 * multi-aircraft ESN selection survives rather than becoming "a,b".
 */
export const buildAffQuery = (params: AffQueryParams): URLSearchParams => {
  const qs = new URLSearchParams();

  for (const key of AFF_QUERY_KEYS) {
    const value = params[key];
    if (value == null || value === '') continue;

    // A multi-aircraft selection repeats the key so Express yields an array.
    if (Array.isArray(value)) {
      for (const v of value) qs.append(key, String(v));
    } else {
      qs.append(key, String(value));
    }
  }

  return qs;
};

export class AffService {
  private getBaseUrl(): string {
    const backendUrl = getBackendUrl();
    if (!backendUrl) {
      throw new Error('FATAL: BACKEND_URL not configured in runtime config');
    }
    return `${backendUrl}/api/aff`;
  }

  private async get<T>(path: string, params: AffQueryParams): Promise<AffResponse<T>> {
    const url = `${this.getBaseUrl()}${path}?${buildAffQuery(params).toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async fetchAircraft<T = any>(params: AffQueryParams = {}): Promise<AffResponse<T>> {
    log.debug('AffService: fetching aircraft', params);
    return this.get<T>('/aircraft', params);
  }

  async fetchTracks<T = any>(params: AffQueryParams = {}): Promise<AffResponse<T>> {
    log.debug('AffService: fetching tracks', params);
    return this.get<T>('/tracks', params);
  }

  /**
   * One aircraft by ESN.
   *
   * A track row carries only what aff_tracks holds — no make, model, owner or
   * function — so opening the aircraft from a track row has to fetch the real
   * record rather than build a modal out of the row.
   */
  async fetchAircraftByEsn<T extends { esn?: unknown } = any>(esn: string, includeSim = false): Promise<T | null> {
    // `esn` is an exact match on the router (#866 U4). A tracker moved between
    // airframes can match more than one row; the most recently observed is
    // the aircraft it is on now.
    const res = await this.fetchAircraft<T>({ esn, sortBy: 'positionTimestamp', sortOrder: 'desc', limit: 1, includeSim: includeSim || undefined });
    return res.data[0] ?? null;
  }

  /** Absolute URL for a filtered spatial download — same filters as the grid. */
  downloadUrl(
    context: 'aircraft' | 'tracks',
    params: AffQueryParams,
    format: 'geojson' | 'kml' | 'shapefile',
    crs = 4326,
  ): string {
    const qs = buildAffQuery(params);
    qs.append('format', format);
    qs.append('crs', String(crs));
    return `${this.getBaseUrl()}/${context}/download?${qs.toString()}`;
  }
}

export const affService = new AffService();
export default affService;
